import * as Popover from '@radix-ui/react-popover'
import { Check, ChevronDown, X } from 'lucide-react'
import { useState } from 'react'
import { INVALID_CONTROL } from './Field'
import type { SelectOption } from './Select'
import { cn } from '@/lib/utils'

export interface MultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  options: SelectOption[]
  placeholder?: string
  invalid?: boolean
  id?: string
  searchable?: boolean
}

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select',
  invalid,
  id,
  searchable = true,
}: MultiSelectProps) {
  const [query, setQuery] = useState('')

  // Chips follow selection order, which is meaningful ("main dye first"), not list order.
  const selected = value
    .map((entry) => options.find((option) => option.value === entry))
    .filter((option): option is SelectOption => Boolean(option))
  const visible = query
    ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
    : options

  const toggle = (option: string) =>
    onChange(value.includes(option) ? value.filter((v) => v !== option) : [...value, option])

  return (
    <Popover.Root>
      <Popover.Trigger
        id={id}
        className={cn(
          'flex min-h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-control)]',
          'border border-line bg-paper px-3 py-1.5 text-left text-sm transition-colors',
          'focus:border-brand focus:outline-none',
          invalid && INVALID_CONTROL,
        )}
      >
        {selected.length === 0 ? (
          <span className="text-ink-faint">{placeholder}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {selected.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand"
              >
                {option.label}
                <X
                  className="size-3 cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation()
                    toggle(option.value)
                  }}
                />
              </span>
            ))}
          </span>
        )}
        <ChevronDown className="size-4 shrink-0 text-ink-faint" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-[var(--radius-control)] border border-line bg-paper shadow-[var(--shadow-float)]"
        >
          {searchable && (
            <div className="border-b border-line p-2">
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search..."
                className="w-full rounded-md bg-paper-muted px-2 py-1.5 text-sm outline-none placeholder:text-ink-faint"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto p-1">
            {visible.map((option) => {
              const isSelected = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-ink hover:bg-brand-soft"
                >
                  <span
                    className={cn(
                      'flex size-4 items-center justify-center rounded border',
                      isSelected ? 'border-brand bg-brand text-white' : 'border-line-strong',
                    )}
                  >
                    {isSelected && <Check className="size-3" />}
                  </span>
                  {option.swatch && (
                    <span
                      className="size-3 rounded-full border border-line"
                      style={{ backgroundColor: option.swatch }}
                    />
                  )}
                  {option.label}
                </button>
              )
            })}
            {visible.length === 0 && (
              <p className="px-2 py-3 text-sm text-ink-faint">No matches.</p>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
